import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { majorToMinor, minorToMajor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { usePostJournalEntry } from "@/features/ledger/api/use-post-journal-entry";
import type {
  PostJournalEntryRequestDto,
} from "@/features/ledger/api/dto";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import {
  createPostJournalEntrySchema,
  type PostJournalEntryFormValues,
} from "@/features/ledger/schemas/journal-entry.schema";

const EMPTY_VALUES: PostJournalEntryFormValues = {
  productCode: "",
  providerCode: "",
  sourceService: "wallet-service",
  sourceTransactionRef: "",
  currency: "GNF",
  businessDate: "",
  principal: 0,
  fee: undefined,
  tax: undefined,
  net: undefined,
  agentCommission: undefined,
};

const toPayload = (
  values: PostJournalEntryFormValues
): PostJournalEntryRequestDto => {
  const amounts: PostJournalEntryRequestDto["amounts"] = {
    PRINCIPAL: majorToMinor(values.principal, values.currency),
  };
  if (values.fee != null && values.fee > 0)
    amounts.FEE = majorToMinor(values.fee, values.currency);
  if (values.tax != null && values.tax > 0)
    amounts.TAX = majorToMinor(values.tax, values.currency);
  if (values.net != null && values.net > 0)
    amounts.NET = majorToMinor(values.net, values.currency);
  if (values.agentCommission != null && values.agentCommission > 0)
    amounts.AGENT_COMMISSION = majorToMinor(
      values.agentCommission,
      values.currency
    );
  return {
    productCode: values.productCode.trim(),
    providerCode: values.providerCode?.trim() || null,
    sourceService: values.sourceService.trim(),
    sourceTransactionRef: values.sourceTransactionRef.trim(),
    currency: values.currency,
    businessDate: values.businessDate,
    amounts,
  };
};

/**
 * Comptabilisation manuelle d'une écriture (`POST /JournalEntries/post`,
 * guide §6.4) — réservée à flashpay-service / flashpay-superadmin /
 * flashpay-admin. Idempotency-Key gérée par le hook (§4.7). Preview
 * obligatoire avec section frais (FEE / TAX / AGENT_COMMISSION) et total
 * (NET ou PRINCIPAL).
 */
export function JournalEntryPostDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLedgerTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createPostJournalEntrySchema(), [i18n.language]);

  const post = usePostJournalEntry();
  const [preview, setPreview] = useState<PostJournalEntryRequestDto | null>(
    null
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    post.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("ledger.journal.feedback.posted"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const fieldError =
    (name: keyof PostJournalEntryFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const textField = (
    name: "productCode" | "providerCode" | "sourceService" | "sourceTransactionRef",
    labelKey: string,
    placeholderKey: string,
    withValidator = false
  ) => (
    <form.Field
      name={name}
      {...(withValidator ? { validators: { onChange: fieldError(name) } } : {})}
    >
      {(field) => (
        <div className="space-y-1.5">
          <Label htmlFor={name}>{t(labelKey)}</Label>
          <Input
            id={name}
            className="notranslate"
            placeholder={t(placeholderKey)}
            value={field.state.value ?? ""}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
          />
          {field.state.meta.errors.length > 0 && (
            <p className="text-sm text-destructive">
              {field.state.meta.errors[0]}
            </p>
          )}
        </div>
      )}
    </form.Field>
  );

  const amountField = (
    name: "principal" | "fee" | "tax" | "net" | "agentCommission",
    labelKey: string
  ) => (
    <form.Field
      name={name}
      {...(name === "principal"
        ? { validators: { onChange: fieldError(name) } }
        : {})}
    >
      {(field) => (
        <div className="space-y-1.5">
          <Label htmlFor={name}>{t(labelKey)}</Label>
          <Input
            id={name}
            type="number"
            min={0}
            step="0.01"
            className="notranslate"
            placeholder="0"
            value={field.state.value ?? ""}
            onBlur={field.handleBlur}
            onChange={(e) =>
              field.handleChange(
                Number.isNaN(e.target.valueAsNumber)
                  ? undefined
                  : e.target.valueAsNumber
              )
            }
          />
          {field.state.meta.errors.length > 0 && (
            <p className="text-sm text-destructive">
              {field.state.meta.errors[0]}
            </p>
          )}
        </div>
      )}
    </form.Field>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("ledger.journal.post")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            {textField(
              "productCode",
              "ledger.journal.form.productCode",
              "ledger.journal.form.productCodePlaceholder",
              true
            )}
            {textField(
              "providerCode",
              "ledger.journal.form.providerCode",
              "ledger.journal.form.providerCodePlaceholder"
            )}
            {textField(
              "sourceService",
              "ledger.journal.form.sourceService",
              "ledger.journal.form.sourceServicePlaceholder",
              true
            )}
            {textField(
              "sourceTransactionRef",
              "ledger.journal.form.sourceTransactionRef",
              "ledger.journal.form.sourceTransactionRefPlaceholder",
              true
            )}

            <form.Field
              name="currency"
              validators={{ onChange: fieldError("currency") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="currency">
                    {t("ledger.journal.form.currency")}
                  </Label>
                  <Input
                    id="currency"
                    className="uppercase notranslate"
                    maxLength={3}
                    placeholder={t("ledger.journal.form.currencyPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="businessDate"
              validators={{ onChange: fieldError("businessDate") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="businessDate">
                    {t("ledger.journal.form.businessDate")}
                  </Label>
                  <Input
                    id="businessDate"
                    type="date"
                    className="notranslate"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {amountField("principal", "ledger.journal.form.principal")}
            {amountField("fee", "ledger.journal.form.fee")}
            {amountField("tax", "ledger.journal.form.tax")}
            {amountField("net", "ledger.journal.form.net")}
            {amountField(
              "agentCommission",
              "ledger.journal.form.agentCommission"
            )}

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={post.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={post.isPending}>
                {post.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("ledger.journal.preview.postTitle")}
          description={t("ledger.journal.preview.postDescription")}
          mode="user"
          fees={[
            ...(preview.amounts.FEE != null
              ? [
                  {
                    label: t("ledger.journal.form.fee"),
                    amount: minorToMajor(preview.amounts.FEE, preview.currency ?? "GNF"),
                    currency: preview.currency ?? "GNF",
                  },
                ]
              : []),
            ...(preview.amounts.TAX != null
              ? [
                  {
                    label: t("ledger.journal.form.tax"),
                    amount: minorToMajor(preview.amounts.TAX, preview.currency ?? "GNF"),
                    currency: preview.currency ?? "GNF",
                  },
                ]
              : []),
            ...(preview.amounts.AGENT_COMMISSION != null
              ? [
                  {
                    label: t("ledger.journal.form.agentCommission"),
                    amount: minorToMajor(
                      preview.amounts.AGENT_COMMISSION,
                      preview.currency ?? "GNF"
                    ),
                    currency: preview.currency ?? "GNF",
                  },
                ]
              : []),
          ]}
          total={{
            label: t("ledger.journal.preview.totalLabel"),
            amount: minorToMajor(
              preview.amounts.NET ?? preview.amounts.PRINCIPAL ?? 0,
              preview.currency ?? "GNF"
            ),
            currency: preview.currency ?? "GNF",
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !post.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: post.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
